import { computed, type ComputedRef, toValue } from 'vue'
import { type Routes } from '@/router/routes'
import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'
import type {
    JewelleryBraceletProductStyle,
    JewelleryDesignStyle,
    JewelleryEarringsProductStyle,
    JewelleryGemstoneCode,
    JewelleryNecklaceProductStyle,
    JewelleryRingProductStyle,
    MetalFilterMetalFragment,
} from '@/graphql/api'
import type { JewelleryProductType } from '@/router'
import { assertNever, type RefOrGetter } from '@/helpers'

type Label<I extends string> = {
    systemCode: I
    translation: {
        name: string
    }
}

const getProductTypeName = (productType: JewelleryProductType) => {
    switch (productType) {
        case 'BRACELET':
            return 'Bracelets'
        case 'EARRINGS':
            return 'Earrings'
        case 'NECKLACE':
            return 'Necklaces'
        case 'RING':
            return 'Rings'
        default:
            return assertNever(productType, '')
    }
}

export function getJewelleryBreadcrumbs(
    {
        productType,
        productStyle,
        gemstone,
        designStyle,
        metal,
        product,
    }: {
        productType: RefOrGetter<JewelleryProductType | undefined>
        productStyle: RefOrGetter<
            | Label<
                  | JewelleryBraceletProductStyle
                  | JewelleryEarringsProductStyle
                  | JewelleryNecklaceProductStyle
                  | JewelleryRingProductStyle
              >
            | undefined
        >
        gemstone: RefOrGetter<Label<JewelleryGemstoneCode> | undefined>
        designStyle: RefOrGetter<Label<JewelleryDesignStyle> | undefined>
        metal: RefOrGetter<MetalFilterMetalFragment[]>
        product: RefOrGetter<Breadcrumb | undefined> | undefined
    },
    routes: Routes
): ComputedRef<Breadcrumb[]> {
    const baseBreadcrumbs: Breadcrumb[] = [
        {
            title: 'Home',
            to: routes.home(),
        },
        {
            title: 'Jewellery',
            to: routes.jewelleries(),
        },
    ]

    return computed<Breadcrumb[]>(() => {
        const b: Breadcrumb[] = [...baseBreadcrumbs]

        const routeParams: Parameters<typeof routes.jewelleries>[0] = {}

        const productTypeValue = toValue(productType)
        if (productTypeValue) {
            routeParams.productType = productTypeValue
            b.push({
                title: getProductTypeName(productTypeValue),
                to: routes.jewelleries(routeParams),
            })
        }

        const productStyleValue = toValue(productStyle)
        if (productStyleValue) {
            routeParams.productStyle = productStyleValue?.systemCode
            b.push({
                title: productStyleValue.translation?.name,
                to: routes.jewelleries(routeParams),
            })
        }

        const gemstoneValue = toValue(gemstone)
        if (gemstoneValue) {
            routeParams.gemstone = gemstoneValue.systemCode
            b.push({
                title: gemstoneValue.translation?.name,
                to: routes.jewelleries(routeParams),
            })
        }

        const designStyleValue = toValue(designStyle)
        if (designStyleValue) {
            routeParams.designStyle = designStyleValue.systemCode
            b.push({
                title: designStyleValue.translation?.name,
                to: routes.jewelleries(routeParams),
            })
        }

        const metalValue = toValue(metal)

        if (metalValue.length <= 1) {
            for (const metal of metalValue) {
                const label = metal.translation.name

                routeParams.metal = metal.systemCode
                b.push({
                    title: label,
                    to: routes.jewelleries(routeParams),
                })
            }
        }

        const productValue = toValue(product)
        if (productValue) {
            b.push(productValue)
        }

        return b
    })
}
